import React, { useEffect, useMemo, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ChevronRight, Check, ArrowRight, Upload, Info, X, FileText, HelpCircle, QrCode, Tag, Barcode, Pencil, Download, Truck, Package, Sparkles, Layers, Ruler, ShoppingCart, Palette, Mail, FileCheck, PackageCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { DataSheetModal } from '../components/DataSheetModal';
interface AssetLabelsPageProps {
  onOpenSampleModal?: () => void;
}
// --- TYPES ---
type AssetTypeOption = 'asset-barcode' | 'property-of' | 'asset-qr' | 'create-own' | null;
type MaterialOption = 'pvc' | 'polyester' | 'mark-resist' | null;
type ColourOption = 'white' | 'silver' | null;
type FinishOption = 'no-laminate' | 'gloss' | 'matt' | null;
type SizeOption = '40x20' | '50x25' | '60x40' | null;
type QuantityOption = 100 | 200 | 300 | 400 | 500 | 600 | null;
interface DesignVariant {
  id: string;
  label: string;
  image: string;
  size?: SizeOption; // optional embedded size for barcode designs
}
interface AssetType {
  id: AssetTypeOption;
  label: string;
  desc: string;
  designs: DesignVariant[];
  designIncludesSize: boolean; // whether choosing a design also selects a size
}
// --- DATA ---
const assetTypes: AssetType[] = [{
  id: 'asset-barcode',
  label: 'Asset barcode',
  desc: 'Standard barcode identification label',
  designIncludesSize: true,
  designs: [{
    id: 'barcode-40x20',
    label: '40 × 20 mm',
    size: '40x20',
    image: 'https://cdn.magicpatterns.com/uploads/rDdVguduk1sTeqdwxt9qE9/asset-barcode-40x20-design-option-1.png'
  }, {
    id: 'barcode-50x25',
    label: '50 × 25 mm',
    size: '50x25',
    image: 'https://cdn.magicpatterns.com/uploads/jbiTWgRxmSchavzXrEYhZA/asset-barcode-50x25-design-option-1.png'
  }, {
    id: 'barcode-60x40',
    label: '60 × 40 mm',
    size: '60x40',
    image: 'https://cdn.magicpatterns.com/uploads/rCPQBtAEN3o8prh4urA2um/asset-barcode-60x40-design-option-1.png'
  }]
}, {
  id: 'property-of',
  label: 'Property of',
  desc: 'Ownership identification label',
  designIncludesSize: true,
  designs: [{
    id: 'property-40x20',
    label: '40 × 20 mm',
    size: '40x20',
    image: 'https://cdn.magicpatterns.com/uploads/aZBEnmxfu2HAJ6tgPZ7rwT/Screenshot_2026-02-20_at_13.45.59.png'
  }, {
    id: 'property-50x25',
    label: '50 × 25 mm',
    size: '50x25',
    image: 'https://cdn.magicpatterns.com/uploads/aZBEnmxfu2HAJ6tgPZ7rwT/Screenshot_2026-02-20_at_13.45.59.png'
  }, {
    id: 'property-60x40',
    label: '60 × 40 mm',
    size: '60x40',
    image: 'https://cdn.magicpatterns.com/uploads/aZBEnmxfu2HAJ6tgPZ7rwT/Screenshot_2026-02-20_at_13.45.59.png'
  }]
}, {
  id: 'asset-qr',
  label: 'Asset QR',
  desc: 'QR code for digital asset tracking',
  designIncludesSize: true,
  designs: [{
    id: 'qr-40x20',
    label: '40 × 20 mm',
    size: '40x20',
    image: 'https://cdn.magicpatterns.com/uploads/d9oTNWiR2aVzqB1nGvdrUs/Screenshot_2026-02-20_at_13.46.03.png'
  }, {
    id: 'qr-50x25',
    label: '50 × 25 mm',
    size: '50x25',
    image: 'https://cdn.magicpatterns.com/uploads/d9oTNWiR2aVzqB1nGvdrUs/Screenshot_2026-02-20_at_13.46.03.png'
  }, {
    id: 'qr-60x40',
    label: '60 × 40 mm',
    size: '60x40',
    image: 'https://cdn.magicpatterns.com/uploads/d9oTNWiR2aVzqB1nGvdrUs/Screenshot_2026-02-20_at_13.46.03.png'
  }]
}, {
  id: 'create-own',
  label: 'Create your own',
  desc: 'Upload your own artwork or design',
  designIncludesSize: false,
  designs: []
}];
const materials = [{
  id: 'pvc' as const,
  label: 'PVC',
  desc: 'Cost-effective, versatile general-purpose PVC'
}, {
  id: 'polyester' as const,
  label: 'Polyester',
  desc: 'Durable polyester with excellent chemical and heat resistance'
}, {
  id: 'mark-resist' as const,
  label: 'Mark Resist',
  desc: 'Anti-graffiti PVC that resists pen and marker damage'
}];
const colours = [{
  id: 'white' as const,
  label: 'White',
  desc: 'Clean white polyester for clear print visibility'
}, {
  id: 'silver' as const,
  label: 'Silver',
  desc: 'Premium metallic finish for professional identification'
}];
const finishes = [{
  id: 'no-laminate' as const,
  label: 'No laminate'
}, {
  id: 'gloss' as const,
  label: 'Gloss laminated'
}, {
  id: 'matt' as const,
  label: 'Matt laminated'
}];
const sizes = [{
  id: '40x20' as const,
  label: '40 × 20 mm'
}, {
  id: '50x25' as const,
  label: '50 × 25 mm'
}, {
  id: '60x40' as const,
  label: '60 × 40 mm'
}];
const quantities: QuantityOption[] = [100, 200, 300, 400, 500, 600];
// --- PRICING DATA ---
const PRICING_MATRIX: Record<string, number[]> = {
  'pvc-white-no-laminate-40x20': [71.0, 39.0, 28.33, 23.0, 21.6, 19.33],
  'pvc-white-no-laminate-50x25': [75.0, 40.5, 28.0, 26.0, 22.4, 20.0],
  'pvc-white-no-laminate-60x40': [75.0, 44.0, 30.67, 26.0, 22.4, 21.67],
  'pvc-white-gloss-40x20': [78.0, 43.0, 31.17, 25.3, 23.8, 21.33],
  'pvc-white-gloss-50x25': [82.0, 44.5, 30.67, 28.5, 24.6, 22.0],
  'pvc-white-gloss-60x40': [82.0, 48.0, 33.67, 28.5, 24.6, 23.83],
  'pvc-white-matt-40x20': [82.0, 48.0, 33.67, 28.5, 24.6, 23.83],
  'pvc-white-matt-50x25': [82.0, 44.5, 30.67, 28.5, 24.6, 22.0],
  'pvc-white-matt-60x40': [78.0, 43.0, 31.17, 25.3, 23.8, 21.33],
  'polyester-white-gloss-40x20': [81.0, 44.5, 32.33, 26.5, 24.6, 22.0],
  'polyester-white-gloss-50x25': [85.0, 46.0, 32.0, 29.5, 25.4, 23.0],
  'polyester-white-gloss-60x40': [85.0, 50.0, 35.0, 29.5, 25.4, 24.5],
  'polyester-white-matt-40x20': [81.0, 44.5, 32.33, 26.5, 24.6, 22.0],
  'polyester-white-matt-50x25': [85.0, 46.0, 32.0, 29.5, 25.4, 23.0],
  'polyester-white-matt-60x40': [85.0, 50.0, 35.0, 29.5, 25.4, 24.5],
  'polyester-silver-gloss-40x20': [84.0, 46.0, 33.5, 27.5, 25.4, 22.83],
  'polyester-silver-gloss-50x25': [88.0, 47.5, 33.0, 30.5, 26.2, 23.83],
  'polyester-silver-gloss-60x40': [88.0, 52.0, 36.33, 30.5, 26.2, 25.33],
  'polyester-silver-matt-40x20': [84.0, 46.0, 33.5, 27.5, 25.4, 22.83],
  'polyester-silver-matt-50x25': [88.0, 47.5, 33.0, 30.5, 26.2, 23.83],
  'polyester-silver-matt-60x40': [88.0, 52.0, 36.33, 30.5, 26.2, 25.33],
  'mark-resist-no-laminate-40x20': [88.0, 48.0, 34.83, 28.5, 26.2, 23.67],
  'mark-resist-no-laminate-50x25': [92.0, 49.5, 34.33, 31.5, 27.0, 24.67],
  'mark-resist-no-laminate-60x40': [92.0, 54.0, 37.67, 31.5, 27.0, 26.17]
};
const SKU_MATRIX: Record<string, string> = {
  'pvc-white-no-laminate-40x20': '41138',
  'pvc-white-no-laminate-50x25': '41139',
  'pvc-white-no-laminate-60x40': '41140',
  'pvc-white-gloss-40x20': '41141',
  'pvc-white-gloss-50x25': '41142',
  'pvc-white-gloss-60x40': '41143',
  'pvc-white-matt-40x20': '41144',
  'pvc-white-matt-50x25': '41145',
  'pvc-white-matt-60x40': '41146',
  'polyester-white-gloss-40x20': '41147',
  'polyester-white-gloss-50x25': '41148',
  'polyester-white-gloss-60x40': '41149',
  'polyester-white-matt-40x20': '41150',
  'polyester-white-matt-50x25': '41151',
  'polyester-white-matt-60x40': '41152',
  'polyester-silver-gloss-40x20': '41153',
  'polyester-silver-gloss-50x25': '41154',
  'polyester-silver-gloss-60x40': '41155',
  'polyester-silver-matt-40x20': '41156',
  'polyester-silver-matt-50x25': '41157',
  'polyester-silver-matt-60x40': '41158',
  'mark-resist-no-laminate-40x20': '41159',
  'mark-resist-no-laminate-50x25': '41160',
  'mark-resist-no-laminate-60x40': '41161'
};
const VARIATION_DESCRIPTIONS: Record<string, string> = {
  'pvc-white-no-laminate': 'DuraLabel™ PVC no laminate',
  'pvc-white-gloss': 'DuraLabel™ PVC + gloss laminated',
  'pvc-white-matt': 'DuraLabel™ PVC + matt laminated',
  'polyester-white-gloss': 'DuraLabel™ Polyester white gloss laminated',
  'polyester-white-matt': 'DuraLabel™ Polyester white matt laminated',
  'polyester-silver-gloss': 'DuraLabel™ Polyester silver gloss laminated',
  'polyester-silver-matt': 'DuraLabel™ Polyester silver matt laminated',
  'mark-resist-no-laminate': 'DuraLabel™ Mark Resist PVC'
};
const WHATS_INCLUDED: Record<string, string[]> = {
  pvc: ['Durable white PVC', 'Permanent adhesive', 'Indoor / light industrial use', 'UV stable digital print', 'Custom artwork supported', 'Free UK delivery'],
  polyester: ['Industrial polyester face stock', 'Enhanced durability', 'Gloss laminated or matt laminated protective surface', 'Chemical and heat resistant', 'UV stable digital print', 'Free UK delivery'],
  'mark-resist': ['Anti-graffiti surface', 'Resistant to solvent markers', 'Industrial-grade adhesive', 'UV stable digital print', 'Custom artwork supported', 'Free UK delivery'],
  default: ['Durable construction', 'Multiple size options', 'Weather resistant', 'UV stable printing', 'Easy application', 'Free UK delivery']
};
const MATERIAL_SUMMARIES: Record<string, string> = {
  pvc: 'Cost-effective white PVC asset labels ideal for indoor asset tracking, equipment identification, and general-purpose labelling. Suitable for office furniture, IT equipment, and warehouse inventory.',
  'polyester-white': 'Premium white polyester asset labels engineered for demanding environments. Excellent resistance to chemicals, heat, and UV exposure. Ideal for manufacturing equipment, outdoor assets, and laboratory instruments.',
  'polyester-silver': 'Professional silver polyester asset labels with a metallic finish for high-visibility asset identification. Superior durability in harsh industrial conditions. Perfect for machinery, tools, and high-value equipment.',
  'mark-resist': 'Specialist mark-resistant PVC labels that prevent tampering via pen or pencil marking. Designed for secure asset tracking in environments where label integrity is critical.',
  default: 'Durable, custom printed asset labels suitable for industrial, commercial and equipment identification. Select your material to see detailed specifications.'
};
const MATERIAL_SPECS: Record<string, Array<{
  label: string;
  value: string;
}>> = {
  pvc: [{
    label: 'Material',
    value: 'White PVC'
  }, {
    label: 'Thickness',
    value: '100 micron'
  }, {
    label: 'Adhesive',
    value: 'Permanent acrylic'
  }, {
    label: 'Print Method',
    value: 'UV digital print'
  }, {
    label: 'Temp Range',
    value: '-20°C to +80°C'
  }, {
    label: 'Application',
    value: 'Indoor/sheltered outdoor'
  }],
  'polyester-white': [{
    label: 'Material',
    value: 'White Polyester'
  }, {
    label: 'Thickness',
    value: '50 micron'
  }, {
    label: 'Adhesive',
    value: 'Permanent acrylic'
  }, {
    label: 'Print Method',
    value: 'UV digital print'
  }, {
    label: 'Temp Range',
    value: '-40°C to +150°C'
  }, {
    label: 'Application',
    value: 'Indoor/outdoor, chemical environments'
  }],
  'polyester-silver': [{
    label: 'Material',
    value: 'Silver Polyester'
  }, {
    label: 'Thickness',
    value: '50 micron'
  }, {
    label: 'Adhesive',
    value: 'Permanent acrylic'
  }, {
    label: 'Print Method',
    value: 'UV digital print'
  }, {
    label: 'Temp Range',
    value: '-40°C to +150°C'
  }, {
    label: 'Application',
    value: 'Indoor/outdoor, industrial environments'
  }],
  'mark-resist': [{
    label: 'Material',
    value: 'Mark Resist PVC'
  }, {
    label: 'Thickness',
    value: '120 micron'
  }, {
    label: 'Adhesive',
    value: 'Permanent acrylic'
  }, {
    label: 'Print Method',
    value: 'UV digital print'
  }, {
    label: 'Temp Range',
    value: '-20°C to +80°C'
  }, {
    label: 'Application',
    value: 'Secure environments, tamper-evident'
  }]
};
// --- MODAL COMPONENT ---
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-625">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col" data-id="element-626">
        <div className="flex items-center justify-between p-6 border-b border-slate-100" data-id="element-627">
          <h3 className="text-xl font-bold text-[#0A1628]" data-id="element-628">{title}</h3>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-629">
            <X className="h-5 w-5" data-id="element-630" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto" data-id="element-631">{children}</div>
      </motion.div>
    </div>;
}
// --- MAIN COMPONENT ---
export function AssetLabelsPage({
  onOpenSampleModal
}: AssetLabelsPageProps) {
  const [selectedAssetType, setSelectedAssetType] = useState<AssetTypeOption>(null);
  const [selectedDesign, setSelectedDesign] = useState<DesignVariant | null>(null);
  const [selectedSize, setSelectedSize] = useState<SizeOption>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialOption>(null);
  const [selectedColour, setSelectedColour] = useState<ColourOption>(null);
  const [selectedFinish, setSelectedFinish] = useState<FinishOption>(null);
  const [selectedQuantity, setSelectedQuantity] = useState<QuantityOption>(100);
  const [uploadedLogo, setUploadedLogo] = useState<File | null>(null);
  const [manualBarcode, setManualBarcode] = useState('');
  const [isMaterialGuideOpen, setIsMaterialGuideOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isDataSheetOpen, setIsDataSheetOpen] = useState(false);
  const [isCustomSizeOpen, setIsCustomSizeOpen] = useState(false);
  const [isDesignOwnOpen, setIsDesignOwnOpen] = useState(false);
  const [customSizeForm, setCustomSizeForm] = useState({
    width: '',
    height: '',
    name: '',
    email: '',
    notes: ''
  });
  const [designOwnForm, setDesignOwnForm] = useState({
    name: '',
    email: '',
    phone: '',
    labelType: '',
    size: '',
    quantity: '',
    notes: ''
  });
  // Derived
  const currentAssetType = assetTypes.find(t => t.id === selectedAssetType) || null;
  const showDesignStep = selectedAssetType !== null;
  // Whether the design step also selects size (e.g. barcode)
  const designIncludesSize = currentAssetType?.designIncludesSize ?? false;
  // Whether design is selected (or create-own which has no designs)
  const designSelected = selectedAssetType === 'create-own' || selectedDesign !== null;
  // Whether we need a separate size step
  const needsSeparateSizeStep = !designIncludesSize && designSelected;
  // Whether size is resolved (either from design or separate selection)
  const sizeResolved = selectedSize !== null;
  // Auto-set size from design when design includes size
  useEffect(() => {
    if (designIncludesSize && selectedDesign?.size) {
      setSelectedSize(selectedDesign.size);
    }
  }, [selectedDesign, designIncludesSize]);
  // Internal material key
  const internalMaterialKey = useMemo(() => {
    if (!selectedMaterial) return null;
    if (selectedMaterial === 'pvc') return 'pvc-white';
    if (selectedMaterial === 'polyester' && selectedColour) return `polyester-${selectedColour}`;
    if (selectedMaterial === 'mark-resist') return 'mark-resist';
    return null;
  }, [selectedMaterial, selectedColour]);
  const resolvedFinish = useMemo(() => {
    if (selectedMaterial === 'mark-resist') return 'no-laminate';
    return selectedFinish;
  }, [selectedMaterial, selectedFinish]);
  const showColourStep = selectedMaterial === 'polyester';
  const availableFinishes = useMemo(() => {
    if (selectedMaterial === 'pvc') return ['no-laminate', 'gloss', 'matt'];
    if (selectedMaterial === 'polyester' && selectedColour) return ['gloss', 'matt'];
    if (selectedMaterial === 'mark-resist') return [];
    return [];
  }, [selectedMaterial, selectedColour]);
  const showFinishStep = selectedMaterial && selectedMaterial !== 'mark-resist' && (selectedMaterial === 'pvc' || selectedMaterial === 'polyester' && selectedColour !== null);
  const finishResolved = selectedMaterial === 'mark-resist' || selectedFinish && availableFinishes.includes(selectedFinish);
  useEffect(() => {
    if (selectedMaterial === 'mark-resist') setSelectedFinish('no-laminate');
  }, [selectedMaterial]);
  useEffect(() => {
    if (selectedMaterial !== 'polyester') setSelectedColour(null);
  }, [selectedMaterial]);
  useEffect(() => {
    if (selectedMaterial && selectedMaterial !== 'mark-resist' && selectedFinish && !availableFinishes.includes(selectedFinish)) {
      setSelectedFinish(null);
    }
  }, [selectedMaterial, selectedColour, availableFinishes, selectedFinish]);
  const skuKey = useMemo(() => {
    if (!internalMaterialKey || !resolvedFinish || !selectedSize) return null;
    return `${internalMaterialKey}-${resolvedFinish}-${selectedSize}`;
  }, [internalMaterialKey, resolvedFinish, selectedSize]);
  const pricingData = useMemo(() => {
    if (!skuKey) return null;
    const sku = SKU_MATRIX[skuKey];
    if (!sku) return null;
    const prices = PRICING_MATRIX[skuKey];
    if (!prices) return null;
    const qty = selectedQuantity || 100;
    const quantityIndex = quantities.indexOf(qty);
    if (quantityIndex === -1) return null;
    const pricePer100 = prices[quantityIndex];
    const totalPrice = (pricePer100 * (qty / 100)).toFixed(2);
    return {
      pricePer100: pricePer100.toFixed(2),
      totalPrice,
      sku
    };
  }, [skuKey, selectedQuantity]);
  const currentSku = useMemo(() => {
    if (!skuKey) return null;
    return SKU_MATRIX[skuKey] || null;
  }, [skuKey]);
  const variationDesc = useMemo(() => {
    if (!internalMaterialKey || !resolvedFinish) return null;
    return VARIATION_DESCRIPTIONS[`${internalMaterialKey}-${resolvedFinish}`] || null;
  }, [internalMaterialKey, resolvedFinish]);
  const includesItems = useMemo(() => {
    if (!selectedMaterial) return WHATS_INCLUDED['default'];
    return WHATS_INCLUDED[selectedMaterial] || WHATS_INCLUDED['default'];
  }, [selectedMaterial]);
  const specsKey = useMemo(() => {
    if (!selectedMaterial) return null;
    if (selectedMaterial === 'pvc') return 'pvc';
    if (selectedMaterial === 'polyester' && selectedColour) return `polyester-${selectedColour}`;
    if (selectedMaterial === 'mark-resist') return 'mark-resist';
    return selectedMaterial;
  }, [selectedMaterial, selectedColour]);
  const productSummary = specsKey ? MATERIAL_SUMMARIES[specsKey] || MATERIAL_SUMMARIES['default'] : MATERIAL_SUMMARIES['default'];
  const specs = specsKey ? MATERIAL_SPECS[specsKey] || null : null;
  // Handlers
  const handleAssetTypeSelect = (typeId: AssetTypeOption) => {
    setSelectedAssetType(typeId);
    setSelectedDesign(null);
    setSelectedSize(null); // reset size when asset type changes
  };
  const handleDesignSelect = (design: DesignVariant) => {
    setSelectedDesign(design);
    // If design includes size, auto-set it
    if (design.size) {
      setSelectedSize(design.size);
    }
  };
  const handleMaterialSelect = (matId: MaterialOption) => {
    setSelectedMaterial(matId);
    setSelectedColour(null);
    setSelectedFinish(null);
  };
  const handleColourSelect = (colId: ColourOption) => {
    setSelectedColour(colId);
    setSelectedFinish(null);
  };
  const isConfigComplete = internalMaterialKey && resolvedFinish && selectedSize && selectedQuantity;
  useEffect(() => {
    document.title = 'Industrial Asset Labels | Industrial Labels & Nameplates';
  }, []);
  const getProductImage = () => {
    if (selectedDesign) return selectedDesign.image;
    return 'https://cdn.magicpatterns.com/uploads/pxBMXm56Ej1CvLKsN6yqji/ILN_IMAGES.jpg';
  };
  // Dynamic step numbering
  const getStepNumbers = () => {
    let n = 1;
    const steps: Record<string, number> = {};
    steps.assetType = n++;
    steps.designStyle = n++;
    if (needsSeparateSizeStep) steps.size = n++;
    steps.material = n++;
    if (showColourStep) steps.colour = n++;
    if (showFinishStep) steps.finish = n++;
    steps.quantity = n++;
    return steps;
  };
  const stepNums = getStepNumbers();
  const materialDisplayLabel = useMemo(() => {
    if (!selectedMaterial) return '';
    const mat = materials.find(m => m.id === selectedMaterial);
    const label = mat?.label || '';
    if (selectedMaterial === 'polyester' && selectedColour) {
      return `${label} ${selectedColour.charAt(0).toUpperCase() + selectedColour.slice(1)}`;
    }
    return label;
  }, [selectedMaterial, selectedColour]);
  // Design step label
  const designStepLabel = designIncludesSize ? 'Choose design style and size' : 'Choose design style';
  return <div className="min-h-screen bg-white font-sans text-[#0A1628] selection:bg-[#0052CC] selection:text-white overflow-x-hidden" data-id="element-632">
      <Header onOpenSampleModal={onOpenSampleModal} data-id="element-633" />

      <main className="pt-[135px] md:pt-[145px]" data-id="element-634">
        {/* Breadcrumb */}
        <div className="border-b border-slate-100" data-id="element-635">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-3" data-id="element-636">
            <nav className="flex items-center gap-2 text-sm" data-id="element-637">
              <a href="/" className="text-slate-400 hover:text-[#0052CC] transition-colors" data-id="element-638">
                Home
              </a>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300" data-id="element-639" />
              <a href="/products" className="text-slate-400 hover:text-[#0052CC] transition-colors" data-id="element-640">
                Identification labels
              </a>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300" data-id="element-641" />
              <span className="text-[#0A1628]" data-id="element-642">Asset labels</span>
            </nav>
          </div>
        </div>

        {/* Introduction */}
        <section className="py-16 md:py-24 border-b border-slate-100 bg-gradient-to-b from-slate-50/80 to-white" data-id="element-643">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-644">
            <div className="flex items-center gap-2 mb-8" data-id="element-645">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#0052CC]/10 text-[#0052CC] text-xs font-bold rounded-full uppercase tracking-wider" data-id="element-646">
                <Sparkles className="h-3 w-3" data-id="element-647" />
                DuraLabel™
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-24 items-start mb-16" data-id="element-648">
              <div data-id="element-649">
                <h1 className="text-3xl md:text-4xl lg:text-[3.25rem] font-extrabold text-[#0A1628] tracking-tight leading-[1.2]" data-id="element-650">
                  Industrial asset labels for equipment &amp; asset tracking
                </h1>
              </div>
              <div className="lg:pt-3" data-id="element-651">
                <p className="text-base md:text-lg text-slate-500 leading-[1.75]" data-id="element-652">
                  Our durable asset labels are built for industrial environments
                  where standard labels fail. Designed for reliable equipment
                  identification, they withstand temperatures up to 80°C and
                  resist oils, solvents, and abrasion — ideal for factories,
                  warehouses, and workshops.
                </p>
              </div>
            </div>

            {/* How it works */}
            <div className="bg-slate-50 rounded-2xl px-6 md:px-8 py-7 md:py-8 border border-slate-200/60" data-id="element-653">
              <div className="flex items-center gap-2 mb-5" data-id="element-654">
                <div className="h-1 w-5 rounded-full bg-[#0052CC]" data-id="element-655" />
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-656">
                  How it works
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4" data-id="element-657">
                {[{
                icon: <Tag className="h-4 w-4" data-id="element-658" />,
                step: '01',
                label: 'Choose type',
                desc: 'Barcode, QR, or custom'
              }, {
                icon: <Ruler className="h-4 w-4" data-id="element-659" />,
                step: '02',
                label: 'Design & size',
                desc: 'Layout and dimensions'
              }, {
                icon: <Layers className="h-4 w-4" data-id="element-660" />,
                step: '03',
                label: 'Material',
                desc: 'PVC, polyester, or mark resist'
              }, {
                icon: <Palette className="h-4 w-4" data-id="element-661" />,
                step: '04',
                label: 'Finish',
                desc: 'Laminate protection'
              }, {
                icon: <ShoppingCart className="h-4 w-4" data-id="element-662" />,
                step: '05',
                label: 'Order',
                desc: 'Quantity from 100 to 600+'
              }].map((item, i) => <motion.div key={item.step} initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.1 + i * 0.07,
                duration: 0.4,
                ease: [0.25, 0.46, 0.45, 0.94]
              }} className="relative flex items-start gap-3 py-3 px-3.5 rounded-xl bg-white border border-slate-200/80 shadow-sm" data-id="element-663">
                    <div className="flex flex-col items-center gap-1 flex-shrink-0" data-id="element-664">
                      <span className="text-[9px] font-bold text-[#0052CC] tabular-nums" data-id="element-665">
                        {item.step}
                      </span>
                      <div className="h-8 w-8 rounded-lg bg-[#0052CC] text-white flex items-center justify-center shadow-sm shadow-[#0052CC]/20" data-id="element-666">
                        {item.icon}
                      </div>
                    </div>
                    <div className="min-w-0 pt-3" data-id="element-667">
                      <span className="block text-xs font-bold text-[#0A1628] leading-tight" data-id="element-668">
                        {item.label}
                      </span>
                      <span className="block text-[10px] text-slate-400 leading-tight mt-0.5 truncate" data-id="element-669">
                        {item.desc}
                      </span>
                    </div>
                    {i < 4 && <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-10" data-id="element-670">
                        <ChevronRight className="h-3.5 w-3.5 text-slate-300" data-id="element-671" />
                      </div>}
                  </motion.div>)}
              </div>
            </div>
          </div>
        </section>

        {/* Main Product Section */}
        <section className="py-12 md:py-16" data-id="element-672">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-673">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-674">
              {/* Left Side: Image & Info */}
              <div className="relative lg:sticky lg:top-[170px] lg:self-start" data-id="element-675">
                <div className="aspect-[16/10] max-w-md mx-auto lg:max-w-none rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 mb-6 border border-slate-200 shadow-sm" data-id="element-676">
                  <motion.img key={getProductImage()} src={getProductImage()} alt="Asset labels" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 0.3
                }} className="w-full h-full object-contain p-6" data-id="element-677" />
                </div>

                <div className="mb-8" data-id="element-678">
                  <h3 className="text-lg font-bold text-[#0A1628] mb-2" data-id="element-679">
                    Product summary
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4" data-id="element-680">
                    {productSummary}
                  </p>
                  {variationDesc && <p className="text-sm font-semibold text-[#0A1628] mb-3" data-id="element-681">
                      Variation: {variationDesc}
                    </p>}
                  {selectedMaterial && <div className="flex items-center gap-4" data-id="element-682">
                      <button onClick={() => setIsDataSheetOpen(true)} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0052CC] hover:underline" data-id="element-683">
                        <FileText className="h-4 w-4" data-id="element-684" />
                        View technical sheet
                      </button>
                      <button onClick={() => setIsDataSheetOpen(true)} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0052CC] hover:underline" data-id="element-685">
                        <Download className="h-4 w-4" data-id="element-686" />
                        Download technical sheet
                      </button>
                    </div>}
                </div>

                {specs && <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden" data-id="element-687">
                    <div className="px-4 py-3 border-b border-slate-200 bg-slate-100/50" data-id="element-688">
                      <h4 className="font-bold text-sm text-[#0A1628]" data-id="element-689">
                        Specifications
                      </h4>
                    </div>
                    <div className="divide-y divide-slate-200" data-id="element-690">
                      {specs.map(spec => <div key={spec.label} className="grid grid-cols-3 px-4 py-2.5 text-sm" data-id="element-691">
                          <span className="text-slate-500 font-medium" data-id="element-692">
                            {spec.label}
                          </span>
                          <span className="col-span-2 text-[#0A1628]" data-id="element-693">
                            {spec.value}
                          </span>
                        </div>)}
                    </div>
                  </div>}
              </div>

              {/* Right Side: Configuration */}
              <div data-id="element-694">
                <div className="mb-8 pb-6 border-b border-slate-100" data-id="element-695">
                  <div className="flex items-center gap-3 mb-2" data-id="element-696">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1628] tracking-tight leading-[1.08]" data-id="element-697">
                      Asset labels
                    </h2>
                    <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full uppercase tracking-wider border border-emerald-200" data-id="element-698">
                      In stock
                    </span>
                  </div>
                  <p className="text-base text-slate-500" data-id="element-699">Brand: DuraLabel™</p>
                </div>

                <div className="space-y-10" data-id="element-700">
                  {/* STEP 1: Asset Type */}
                  <div data-id="element-701">
                    <div className="flex items-center gap-2 mb-4" data-id="element-702">
                      <span className="h-5 w-5 rounded-full bg-[#0A1628] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-703">
                        {stepNums.assetType}
                      </span>
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-704">
                        Choose asset type
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-705">
                      {assetTypes.map(type => {
                      const icons: Record<string, React.ReactNode> = {
                        'asset-barcode': <Barcode className="h-5 w-5" data-id="element-706" />,
                        'property-of': <Tag className="h-5 w-5" data-id="element-707" />,
                        'asset-qr': <QrCode className="h-5 w-5" data-id="element-708" />,
                        'create-own': <Pencil className="h-5 w-5" data-id="element-709" />
                      };
                      const isSelected = selectedAssetType === type.id;
                      const isCreateOwn = type.id === 'create-own';
                      return <motion.button key={type.id} onClick={() => handleAssetTypeSelect(type.id)} whileTap={{
                        scale: 0.97
                      }} className={`p-4 text-left rounded-xl transition-all duration-200 border flex flex-col gap-2 ${isSelected ? 'border-[#0A1628] bg-[#0A1628] text-white' : isCreateOwn ? 'border-[#0052CC] border-2 text-[#0A1628] hover:border-[#0052CC] hover:bg-[#0052CC]/5 bg-[#0052CC]/[0.03]' : 'border-slate-200 text-[#0A1628] hover:border-slate-300 bg-white'}`} data-id="element-710">
                            <div className={`${isSelected ? 'text-white' : 'text-[#0052CC]'}`} data-id="element-711">
                              {icons[type.id as string]}
                            </div>
                            <span className="text-sm font-bold leading-tight" data-id="element-712">
                              {type.label}
                            </span>
                            <span className={`text-[10px] leading-tight ${isSelected ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-713">
                              {type.desc}
                            </span>
                          </motion.button>;
                    })}
                    </div>
                  </div>

                  {/* STEP 2: Design Style (and Size for barcode) */}
                  <AnimatePresence data-id="element-714">
                    {showDesignStep && <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0
                  }} data-id="element-715">
                        <div className="flex items-center gap-2 mb-4" data-id="element-716">
                          <span className="h-5 w-5 rounded-full bg-[#0A1628] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-717">
                            {stepNums.designStyle}
                          </span>
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-718">
                            {designStepLabel}
                          </p>
                        </div>
                        {selectedAssetType === 'create-own' ? <div className="border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 p-8 text-center hover:border-[#0052CC] transition-colors cursor-pointer group" data-id="element-719">
                            <input type="file" className="hidden" id="design-upload" accept=".ai,.eps,.pdf,.png" onChange={e => e.target.files && setUploadedLogo(e.target.files[0])} data-id="element-720" />
                            <label htmlFor="design-upload" className="cursor-pointer block" data-id="element-721">
                              <Upload className="h-10 w-10 text-slate-400 mx-auto mb-4 group-hover:text-[#0052CC] transition-colors" data-id="element-722" />
                              {uploadedLogo ? <p className="text-sm font-medium text-[#0052CC]" data-id="element-723">
                                  {uploadedLogo.name}
                                </p> : <>
                                  <p className="text-base font-medium text-[#0A1628] mb-1" data-id="element-724">
                                    Upload your artwork
                                  </p>
                                  <p className="text-xs text-slate-500" data-id="element-725">
                                    AI, EPS, PDF, High-res PNG (300dpi)
                                  </p>
                                </>}
                            </label>
                          </div> : currentAssetType && currentAssetType.designs.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-726">
                            {currentAssetType.designs.map(design => <motion.button key={design.id} onClick={() => handleDesignSelect(design)} whileTap={{
                        scale: 0.97
                      }} className={`rounded-xl overflow-hidden border-2 transition-all bg-white flex flex-col ${selectedDesign?.id === design.id ? 'border-[#0052CC] ring-2 ring-[#0052CC]/20 shadow-md' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-727">
                                <div className="aspect-[3/2] bg-slate-50 p-2 flex items-center justify-center" data-id="element-728">
                                  <img src={design.image} alt={design.label} className="w-full h-full object-contain" data-id="element-729" />
                                </div>
                                <div className={`px-3 py-2 text-center border-t ${selectedDesign?.id === design.id ? 'bg-[#0052CC] border-[#0052CC]' : 'border-slate-100 bg-white'}`} data-id="element-730">
                                  <span className={`text-xs font-semibold ${selectedDesign?.id === design.id ? 'text-white' : 'text-[#0A1628]'}`} data-id="element-731">
                                    {design.label}
                                  </span>
                                </div>
                              </motion.button>)}
                            {/* Design your own label CTA */}
                            <motion.button onClick={() => setIsDesignOwnOpen(true)} whileTap={{
                        scale: 0.97
                      }} className="rounded-xl border-2 border-dashed border-[#0052CC] bg-[#0052CC]/[0.03] hover:bg-[#0052CC]/[0.07] transition-all flex flex-col items-center justify-center gap-2 p-4 min-h-[100px]" data-id="element-732">
                              <div className="h-9 w-9 rounded-full bg-[#0052CC]/10 flex items-center justify-center" data-id="element-733">
                                <Pencil className="h-4 w-4 text-[#0052CC]" data-id="element-734" />
                              </div>
                              <span className="text-xs font-bold text-[#0052CC] leading-tight text-center" data-id="element-735">
                                Custom size
                              </span>
                            </motion.button>
                          </div> : null}
                      </motion.div>}
                  </AnimatePresence>

                  {/* STEP 3: Size (only for non-barcode types) */}
                  <AnimatePresence data-id="element-736">
                    {needsSeparateSizeStep && <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0
                  }} data-id="element-737">
                        <div className="flex items-center gap-2 mb-4" data-id="element-738">
                          <span className="h-5 w-5 rounded-full bg-[#0A1628] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-739">
                            {stepNums.size}
                          </span>
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-740">
                            Choose size
                          </p>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-741">
                          {sizes.map(size => <motion.button key={size.id} onClick={() => setSelectedSize(size.id)} whileTap={{
                        scale: 0.97
                      }} className={`py-3 px-2 text-center rounded-lg transition-all duration-200 border text-sm font-medium ${selectedSize === size.id ? 'border-[#0A1628] bg-[#0A1628] text-white' : 'border-slate-200 text-[#0A1628] hover:border-slate-300'}`} data-id="element-742">
                              {size.label}
                            </motion.button>)}
                          <motion.button onClick={() => setIsCustomSizeOpen(true)} whileTap={{
                        scale: 0.97
                      }} className="py-3 px-2 text-center rounded-lg transition-all duration-200 border-2 border-dashed border-[#0052CC] text-[#0052CC] text-sm font-semibold hover:bg-[#0052CC]/5 flex items-center justify-center gap-1.5" data-id="element-743">
                            <Pencil className="h-3.5 w-3.5" data-id="element-744" />
                            Custom size
                          </motion.button>
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* STEP: Material */}
                  <AnimatePresence data-id="element-745">
                    {sizeResolved && <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0
                  }} data-id="element-746">
                        <div className="flex items-center justify-between mb-4" data-id="element-747">
                          <div className="flex items-center gap-2" data-id="element-748">
                            <span className="h-5 w-5 rounded-full bg-[#0A1628] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-749">
                              {stepNums.material}
                            </span>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-750">
                              Choose material
                            </p>
                          </div>
                          <button onClick={() => setIsMaterialGuideOpen(true)} className="text-xs font-medium text-[#0052CC] hover:underline flex items-center gap-1" data-id="element-751">
                            <HelpCircle className="h-3 w-3" data-id="element-752" /> Guide
                          </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-753">
                          {materials.map(material => <motion.button key={material.id} onClick={() => handleMaterialSelect(material.id)} whileTap={{
                        scale: 0.97
                      }} className={`p-4 text-left rounded-lg transition-all duration-200 border flex flex-col gap-1 ${selectedMaterial === material.id ? 'border-[#0A1628] bg-[#0A1628] text-white' : 'border-slate-200 text-[#0A1628] hover:border-slate-300'}`} data-id="element-754">
                              <span className="text-sm font-bold" data-id="element-755">
                                {material.label}
                              </span>
                              <span className={`text-[11px] leading-tight ${selectedMaterial === material.id ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-756">
                                {material.desc}
                              </span>
                            </motion.button>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* STEP: Colour (Polyester only) */}
                  <AnimatePresence data-id="element-757">
                    {showColourStep && <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0
                  }} data-id="element-758">
                        <div className="flex items-center gap-2 mb-4" data-id="element-759">
                          <span className="h-5 w-5 rounded-full bg-[#0A1628] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-760">
                            {stepNums.colour}
                          </span>
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-761">
                            Choose colour
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3" data-id="element-762">
                          {colours.map(colour => <motion.button key={colour.id} onClick={() => handleColourSelect(colour.id)} whileTap={{
                        scale: 0.97
                      }} className={`p-4 text-left rounded-lg transition-all duration-200 border flex flex-col gap-1 ${selectedColour === colour.id ? 'border-[#0A1628] bg-[#0A1628] text-white' : 'border-slate-200 text-[#0A1628] hover:border-slate-300'}`} data-id="element-763">
                              <span className="text-sm font-bold" data-id="element-764">
                                {colour.label}
                              </span>
                              <span className={`text-[11px] leading-tight ${selectedColour === colour.id ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-765">
                                {colour.desc}
                              </span>
                            </motion.button>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* STEP: Finish (hidden for Mark Resist) */}
                  <AnimatePresence data-id="element-766">
                    {showFinishStep && availableFinishes.length > 0 && <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0
                  }} data-id="element-767">
                        <div className="flex items-center gap-2 mb-4" data-id="element-768">
                          <span className="h-5 w-5 rounded-full bg-[#0A1628] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-769">
                            {stepNums.finish}
                          </span>
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-770">
                            Choose finish
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-3" data-id="element-771">
                          {finishes.filter(f => availableFinishes.includes(f.id)).map(finish => <motion.button key={finish.id} onClick={() => setSelectedFinish(finish.id as FinishOption)} whileTap={{
                        scale: 0.97
                      }} className={`flex-1 min-w-[120px] px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 border ${selectedFinish === finish.id ? 'border-[#0A1628] bg-[#0A1628] text-white' : 'border-slate-200 text-[#0A1628] hover:border-slate-300'}`} data-id="element-772">
                                {finish.label}
                              </motion.button>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* STEP: Quantity */}
                  <AnimatePresence data-id="element-773">
                    {finishResolved && sizeResolved && <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0
                  }} data-id="element-774">
                        <div className="flex items-center justify-between mb-4" data-id="element-775">
                          <div className="flex items-center gap-2" data-id="element-776">
                            <span className="h-5 w-5 rounded-full bg-[#0A1628] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0" data-id="element-777">
                              {stepNums.quantity}
                            </span>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-778">
                              Choose quantity
                            </p>
                          </div>
                          <button onClick={() => setIsBulkModalOpen(true)} className="text-xs font-medium text-[#0052CC] hover:underline" data-id="element-779">
                            Need more?
                          </button>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-id="element-780">
                          {quantities.map(qty => <motion.button key={qty} onClick={() => setSelectedQuantity(qty)} whileTap={{
                        scale: 0.97
                      }} className={`py-3 px-1 text-center rounded-lg transition-all duration-200 border text-sm font-medium ${selectedQuantity === qty ? 'border-[#0A1628] bg-[#0A1628] text-white' : 'border-slate-200 text-[#0A1628] hover:border-slate-300'}`} data-id="element-781">
                              {qty}
                            </motion.button>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Upload Artwork (after config complete) */}
                  <AnimatePresence data-id="element-782">
                    {isConfigComplete && selectedAssetType !== 'create-own' && <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0
                  }} className="border-t border-slate-100 pt-8 space-y-6" data-id="element-783">
                        <h3 className="text-lg font-bold text-[#0A1628]" data-id="element-784">
                          Upload your artwork
                        </h3>
                        <div data-id="element-785">
                          <p className="text-sm font-semibold text-[#0A1628] mb-2" data-id="element-786">
                            Logo upload
                          </p>
                          <div className="border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 p-8 text-center hover:border-[#0052CC] transition-colors cursor-pointer group" data-id="element-787">
                            <input type="file" className="hidden" id="logo-upload" accept=".ai,.eps,.pdf,.png" onChange={e => e.target.files && setUploadedLogo(e.target.files[0])} data-id="element-788" />
                            <label htmlFor="logo-upload" className="cursor-pointer block" data-id="element-789">
                              <Upload className="h-10 w-10 text-slate-400 mx-auto mb-4 group-hover:text-[#0052CC] transition-colors" data-id="element-790" />
                              {uploadedLogo ? <p className="text-sm font-medium text-[#0052CC]" data-id="element-791">
                                  {uploadedLogo.name}
                                </p> : <>
                                  <p className="text-base font-medium text-[#0A1628] mb-1" data-id="element-792">
                                    Drag & drop or click to upload
                                  </p>
                                  <p className="text-xs text-slate-500" data-id="element-793">
                                    AI, EPS, PDF, High-res PNG (300dpi)
                                  </p>
                                </>}
                            </label>
                          </div>
                          <div className="flex items-center justify-between mt-2" data-id="element-794">
                            <p className="text-[11px] text-slate-400 flex items-center gap-1" data-id="element-795">
                              <Info className="h-3 w-3" data-id="element-796" /> For best print
                              quality, upload vector artwork.
                            </p>
                            <a href="/resources" className="text-xs font-medium text-[#0052CC] hover:underline" data-id="element-797">
                              View artwork guidelines →
                            </a>
                          </div>
                        </div>
                        <div data-id="element-798">
                          <p className="text-sm font-semibold text-[#0A1628] mb-2" data-id="element-799">
                            Insert barcode / asset start number
                          </p>
                          <input type="text" placeholder="Enter barcode number or starting asset number (e.g. 0001)" value={manualBarcode} onChange={e => setManualBarcode(e.target.value)} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-800" />
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Pricing & CTA */}
                  <div className="mt-8 pt-8 border-t border-slate-100" data-id="element-801">
                    {/* Order Summary — WooCommerce-style, above price & CTA */}
                    <AnimatePresence data-id="element-802">
                      {isConfigComplete && <motion.div initial={{
                      opacity: 0,
                      y: 10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} className="mb-6" data-id="element-803">
                          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden" data-id="element-804">
                            <div className="px-4 py-3 border-b border-slate-200 bg-slate-100/60 flex items-center justify-between" data-id="element-805">
                              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-806">
                                Your selection
                              </h4>
                              <span className="text-[10px] text-slate-400" data-id="element-807">
                                Scroll up to edit
                              </span>
                            </div>
                            <div className="divide-y divide-slate-100" data-id="element-808">
                              {/* Asset Type */}
                              {selectedAssetType && <div className="flex items-center justify-between px-4 py-2.5 text-sm" data-id="element-809">
                                  <span className="text-slate-500" data-id="element-810">Type</span>
                                  <span className="font-medium text-[#0A1628]" data-id="element-811">
                                    {assetTypes.find(t => t.id === selectedAssetType)?.label}
                                  </span>
                                </div>}
                              {/* Design & size — show size only */}
                              {selectedSize && <div className="flex items-center justify-between px-4 py-2.5 text-sm" data-id="element-812">
                                  <span className="text-slate-500" data-id="element-813">
                                    Design & size
                                  </span>
                                  <span className="font-medium text-[#0A1628]" data-id="element-814">
                                    {sizes.find(s => s.id === selectedSize)?.label}
                                  </span>
                                </div>}
                              {/* Material */}
                              {selectedMaterial && <div className="flex items-center justify-between px-4 py-2.5 text-sm" data-id="element-815">
                                  <span className="text-slate-500" data-id="element-816">
                                    Material
                                  </span>
                                  <span className="font-medium text-[#0A1628]" data-id="element-817">
                                    {materials.find(m => m.id === selectedMaterial)?.label}
                                  </span>
                                </div>}
                              {/* Colour (Polyester only) */}
                              {selectedMaterial === 'polyester' && selectedColour && <div className="flex items-center justify-between px-4 py-2.5 text-sm" data-id="element-818">
                                    <span className="text-slate-500" data-id="element-819">
                                      Colour
                                    </span>
                                    <span className="font-medium text-[#0A1628]" data-id="element-820">
                                      {colours.find(c => c.id === selectedColour)?.label}
                                    </span>
                                  </div>}
                              {/* Quantity */}
                              {selectedQuantity && <div className="flex items-center justify-between px-4 py-2.5 text-sm" data-id="element-821">
                                  <span className="text-slate-500" data-id="element-822">
                                    Quantity
                                  </span>
                                  <span className="font-medium text-[#0A1628]" data-id="element-823">
                                    {selectedQuantity} labels
                                  </span>
                                </div>}
                              {/* Description (was Variation) */}
                              {variationDesc && <div className="flex items-center justify-between px-4 py-2.5 text-sm bg-[#0A1628]/[0.02]" data-id="element-824">
                                  <span className="text-slate-500" data-id="element-825">
                                    Description
                                  </span>
                                  <span className="font-semibold text-[#0A1628] text-xs" data-id="element-826">
                                    {variationDesc}
                                  </span>
                                </div>}
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>

                    {currentSku && <div className="mb-4" data-id="element-827">
                        <span className="text-xs font-mono text-slate-400" data-id="element-828">
                          SKU: {currentSku}
                        </span>
                      </div>}

                    {isConfigComplete && pricingData && <div className="flex items-end justify-between mb-6" data-id="element-829">
                        <div data-id="element-830">
                          <p className="text-sm text-slate-500 mb-1" data-id="element-831">
                            £{pricingData.pricePer100} per 100 labels
                          </p>
                          <p className="text-xs text-green-600 font-medium" data-id="element-832">
                            Price includes setup and free UK delivery
                          </p>
                        </div>
                        <div className="text-right" data-id="element-833">
                          <p className="text-3xl font-extrabold text-[#0A1628]" data-id="element-834">
                            £{pricingData.totalPrice}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-835">Ex. VAT</p>
                        </div>
                      </div>}

                    <motion.button disabled={!isConfigComplete} whileTap={isConfigComplete ? {
                    scale: 0.98
                  } : {}} className={`w-full py-5 font-bold text-lg rounded-xl transition-all duration-200 ${isConfigComplete ? 'bg-[#0052CC] text-white hover:bg-[#0040A0] shadow-lg shadow-[#0052CC]/20' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`} data-id="element-836">
                      Add to Basket
                    </motion.button>

                    <div className="mt-4 text-center" data-id="element-837">
                      <button onClick={() => setIsBulkModalOpen(true)} className="text-sm font-medium text-slate-500 hover:text-[#0052CC] transition-colors" data-id="element-838">
                        Ordering large quantities?{' '}
                        <span className="text-[#0052CC] underline underline-offset-2" data-id="element-839">
                          Get bulk pricing
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key features + What happens next — combined tighter section */}
        <section className="border-t border-slate-100" data-id="element-840">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-12" data-id="element-841">
            <div className="border border-slate-200 rounded-xl p-8 mb-0" data-id="element-842">
              <p className="font-bold text-[#0A1628] text-lg mb-6" data-id="element-843">
                Key features
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12" data-id="element-844">
                {includesItems.map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-845">
                    <Check className="h-4 w-4 text-[#0A1628] flex-shrink-0" data-id="element-846" />
                    <span className="text-[#0A1628] text-sm" data-id="element-847">{item}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* What happens next? */}
        <section className="bg-gradient-to-br from-[#F0F4FF] via-[#E8EEFB] to-[#F5F0FF]" data-id="element-848">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-12 md:py-14" data-id="element-849">
            <div className="flex items-center gap-2 mb-6" data-id="element-850">
              <div className="h-1 w-5 rounded-full bg-[#0052CC]" data-id="element-851" />
              <p className="text-[10px] font-bold text-[#0052CC]/60 uppercase tracking-widest" data-id="element-852">
                What happens next?
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-853">
              {[{
              icon: <Mail className="h-4 w-4" data-id="element-854" />,
              step: '01',
              label: 'Order confirmation',
              desc: "Once you place your order, you'll receive an email confirmation with your order details."
            }, {
              icon: <FileCheck className="h-4 w-4" data-id="element-855" />,
              step: '02',
              label: 'Artwork approval',
              desc: "If your order includes a custom design, we'll email you a PDF proof for approval before printing. Production will begin once we receive your confirmation. For off-the-shelf products, we'll process your order straight away."
            }, {
              icon: <PackageCheck className="h-4 w-4" data-id="element-856" />,
              step: '03',
              label: 'Production & delivery',
              desc: 'After approval (or immediately for stock items), your order will be prepared and despatched. You can expect to receive your items within 5–7 working days.'
            }].map((item, i) => <motion.div key={item.step} initial={{
              opacity: 0,
              y: 8
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1,
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94]
            }} className="relative flex items-start gap-3.5 py-5 px-5 rounded-2xl bg-white/80 backdrop-blur-sm border border-white shadow-sm" data-id="element-857">
                  <div className="flex flex-col items-center gap-1 flex-shrink-0" data-id="element-858">
                    <span className="text-[9px] font-bold text-[#0052CC] tabular-nums" data-id="element-859">
                      {item.step}
                    </span>
                    <div className="h-9 w-9 rounded-xl bg-[#0052CC] text-white flex items-center justify-center shadow-sm shadow-[#0052CC]/20" data-id="element-860">
                      {item.icon}
                    </div>
                  </div>
                  <div className="min-w-0 pt-1" data-id="element-861">
                    <span className="block text-sm font-bold text-[#0A1628] leading-tight mb-1.5" data-id="element-862">
                      {item.label}
                    </span>
                    <span className="block text-xs text-slate-500 leading-relaxed" data-id="element-863">
                      {item.desc}
                    </span>
                    {i === 2 && <a href="/deliver" className="inline-flex items-center gap-1 text-xs font-semibold text-[#0052CC] mt-2.5 hover:underline" data-id="element-864">
                        Full delivery information
                        <ArrowRight className="h-3 w-3" data-id="element-865" />
                      </a>}
                  </div>
                  {i < 2 && <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-10" data-id="element-866">
                      <ChevronRight className="h-3.5 w-3.5 text-[#0052CC]/30" data-id="element-867" />
                    </div>}
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Supporting Content */}
        <section className="py-16 border-t border-slate-100" data-id="element-868">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-869">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-870">
              {/* Technical Sheet */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0A1628] to-[#142744] p-8 text-white" data-id="element-871">
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10" data-id="element-872">
                  <svg viewBox="0 0 100 100" className="w-full h-full" data-id="element-873">
                    <circle cx="80" cy="20" r="40" fill="currentColor" data-id="element-874" />
                    <circle cx="60" cy="50" r="25" fill="currentColor" data-id="element-875" />
                  </svg>
                </div>
                <div className="relative z-10" data-id="element-876">
                  <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center mb-4" data-id="element-877">
                    <FileText className="h-5 w-5 text-white" data-id="element-878" />
                  </div>
                  <p className="text-lg font-bold mb-2" data-id="element-879">Technical data sheet</p>
                  <p className="text-sm text-slate-300 leading-relaxed mb-6" data-id="element-880">
                    Full material specifications and application guidelines for
                    your selected product.
                  </p>
                  <button onClick={() => setIsDataSheetOpen(true)} className="inline-flex items-center px-5 py-2.5 bg-white text-[#0A1628] font-semibold rounded-full hover:bg-slate-100 transition-colors text-sm group" data-id="element-881">
                    Download sheet
                    <Download className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" data-id="element-882" />
                  </button>
                </div>
              </div>

              {/* Samples */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0052CC] to-[#0040A0] p-8 text-white" data-id="element-883">
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10" data-id="element-884">
                  <svg viewBox="0 0 100 100" className="w-full h-full" data-id="element-885">
                    <rect x="50" y="10" width="45" height="45" rx="8" fill="currentColor" data-id="element-886" />
                    <rect x="35" y="40" width="35" height="35" rx="6" fill="currentColor" data-id="element-887" />
                  </svg>
                </div>
                <div className="relative z-10" data-id="element-888">
                  <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center mb-4" data-id="element-889">
                    <Package className="h-5 w-5 text-white" data-id="element-890" />
                  </div>
                  <p className="text-lg font-bold mb-2" data-id="element-891">Request samples</p>
                  <p className="text-sm text-blue-100 leading-relaxed mb-6" data-id="element-892">
                    Evaluate material quality and finish before placing your
                    order.
                  </p>
                  <button onClick={onOpenSampleModal} className="inline-flex items-center px-5 py-2.5 bg-white text-[#0052CC] font-semibold rounded-full hover:bg-slate-100 transition-colors text-sm group" data-id="element-893">
                    Get samples
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" data-id="element-894" />
                  </button>
                </div>
              </div>

              {/* Delivery */}
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-900 p-8 text-white" data-id="element-895">
                <div className="absolute top-0 right-0 w-32 h-32 opacity-10" data-id="element-896">
                  <svg viewBox="0 0 100 100" className="w-full h-full" data-id="element-897">
                    <circle cx="70" cy="30" r="30" fill="currentColor" data-id="element-898" />
                    <circle cx="50" cy="60" r="20" fill="currentColor" data-id="element-899" />
                  </svg>
                </div>
                <div className="relative z-10" data-id="element-900">
                  <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center mb-4" data-id="element-901">
                    <Truck className="h-5 w-5 text-white" data-id="element-902" />
                  </div>
                  <p className="text-lg font-bold mb-2" data-id="element-903">Free UK delivery</p>
                  <p className="text-sm text-emerald-100 leading-relaxed mb-3" data-id="element-904">
                    Standard orders dispatched within 5–7 working days. Free
                    delivery on all orders, nationwide.
                  </p>
                  <div className="flex items-center gap-2 text-xs text-emerald-200 font-medium" data-id="element-905">
                    <Check className="h-3.5 w-3.5" data-id="element-906" />
                    <span data-id="element-907">No minimum order for free shipping</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Custom Solution CTA */}
        <section className="py-20 bg-[#0A1628]" data-id="element-908">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center" data-id="element-909">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-6" data-id="element-910">
              Need something custom?
            </h2>
            <p className="text-lg text-slate-400 mb-10 max-w-md mx-auto leading-relaxed" data-id="element-911">
              We manufacture bespoke sizes, materials, and finishes to meet your
              exact requirements.
            </p>
            <a href="/contact" className="inline-flex items-center px-8 py-4 bg-white text-[#0A1628] text-base font-bold rounded-full hover:bg-slate-100 transition-colors group" data-id="element-912">
              Get in Touch
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-0.5 transition-transform" data-id="element-913" />
            </a>
          </div>
        </section>
      </main>

      <Footer data-id="element-914" />

      <DataSheetModal isOpen={isDataSheetOpen} onClose={() => setIsDataSheetOpen(false)} imageUrl="https://cdn.magicpatterns.com/uploads/9azdKatRqabVvwJLb3Kpam/Screenshot_2026-02-05_at_01.27.39.png" title={`DuraLabel™ ${materialDisplayLabel || 'Asset Label'} - Technical Data Sheet`} data-id="element-915" />

      {/* Design Your Own Label Modal */}
      <Modal isOpen={isDesignOwnOpen} onClose={() => setIsDesignOwnOpen(false)} title="Design your own label" data-id="element-916">
        <form className="space-y-4" onSubmit={e => {
        e.preventDefault();
        setIsDesignOwnOpen(false);
      }} data-id="element-917">
          <p className="text-sm text-slate-500 mb-4" data-id="element-918">
            Tell us about your ideal label and we'll create a custom design to
            match your exact requirements.
          </p>
          <div className="grid grid-cols-2 gap-3" data-id="element-919">
            <input type="text" placeholder="Your name" value={designOwnForm.name} onChange={e => setDesignOwnForm({
            ...designOwnForm,
            name: e.target.value
          })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-920" />
            <input type="tel" placeholder="Phone number" value={designOwnForm.phone} onChange={e => setDesignOwnForm({
            ...designOwnForm,
            phone: e.target.value
          })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-921" />
          </div>
          <input type="email" placeholder="Email address" value={designOwnForm.email} onChange={e => setDesignOwnForm({
          ...designOwnForm,
          email: e.target.value
        })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-922" />
          <div className="grid grid-cols-2 gap-3" data-id="element-923">
            <div data-id="element-924">
              <label className="block text-xs font-semibold text-slate-700 mb-1" data-id="element-925">
                Label type
              </label>
              <select value={designOwnForm.labelType} onChange={e => setDesignOwnForm({
              ...designOwnForm,
              labelType: e.target.value
            })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] appearance-none" data-id="element-926">
                <option value="" data-id="element-927">Select type</option>
                <option value="barcode" data-id="element-928">Asset barcode</option>
                <option value="qr" data-id="element-929">Asset QR</option>
                <option value="property" data-id="element-930">Property of</option>
                <option value="other" data-id="element-931">Other</option>
              </select>
            </div>
            <div data-id="element-932">
              <label className="block text-xs font-semibold text-slate-700 mb-1" data-id="element-933">
                Preferred size
              </label>
              <input type="text" placeholder="e.g. 70 × 50 mm" value={designOwnForm.size} onChange={e => setDesignOwnForm({
              ...designOwnForm,
              size: e.target.value
            })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-934" />
            </div>
          </div>
          <div data-id="element-935">
            <label className="block text-xs font-semibold text-slate-700 mb-1" data-id="element-936">
              Estimated quantity
            </label>
            <input type="text" placeholder="e.g. 500" value={designOwnForm.quantity} onChange={e => setDesignOwnForm({
            ...designOwnForm,
            quantity: e.target.value
          })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-937" />
          </div>
          <textarea placeholder="Describe your requirements — layout, text, logos, numbering, colours, etc." rows={4} value={designOwnForm.notes} onChange={e => setDesignOwnForm({
          ...designOwnForm,
          notes: e.target.value
        })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] resize-none" data-id="element-938" />
          <button type="submit" className="w-full py-3 bg-[#0052CC] text-white font-bold rounded-xl hover:bg-[#0040A0] transition-colors" data-id="element-939">
            Submit design request
          </button>
        </form>
      </Modal>

      {/* Custom Size Modal */}
      <Modal isOpen={isCustomSizeOpen} onClose={() => setIsCustomSizeOpen(false)} title="Create your own size" data-id="element-940">
        <form className="space-y-4" onSubmit={e => {
        e.preventDefault();
        setIsCustomSizeOpen(false);
      }} data-id="element-941">
          <p className="text-sm text-slate-500 mb-4" data-id="element-942">
            Need a custom size? Fill in your requirements and we'll get back to
            you with a quote.
          </p>
          <div className="grid grid-cols-2 gap-3" data-id="element-943">
            <div data-id="element-944">
              <label className="block text-xs font-semibold text-slate-700 mb-1" data-id="element-945">
                Width (mm)
              </label>
              <input type="number" placeholder="e.g. 70" value={customSizeForm.width} onChange={e => setCustomSizeForm({
              ...customSizeForm,
              width: e.target.value
            })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-946" />
            </div>
            <div data-id="element-947">
              <label className="block text-xs font-semibold text-slate-700 mb-1" data-id="element-948">
                Height (mm)
              </label>
              <input type="number" placeholder="e.g. 50" value={customSizeForm.height} onChange={e => setCustomSizeForm({
              ...customSizeForm,
              height: e.target.value
            })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-949" />
            </div>
          </div>
          <input type="text" placeholder="Your name" value={customSizeForm.name} onChange={e => setCustomSizeForm({
          ...customSizeForm,
          name: e.target.value
        })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-950" />
          <input type="email" placeholder="Email address" value={customSizeForm.email} onChange={e => setCustomSizeForm({
          ...customSizeForm,
          email: e.target.value
        })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]" data-id="element-951" />
          <textarea placeholder="Additional notes (optional)" rows={3} value={customSizeForm.notes} onChange={e => setCustomSizeForm({
          ...customSizeForm,
          notes: e.target.value
        })} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] resize-none" data-id="element-952" />
          <button type="submit" className="w-full py-3 bg-[#0052CC] text-white font-bold rounded-xl hover:bg-[#0040A0] transition-colors" data-id="element-953">
            Submit custom size request
          </button>
        </form>
      </Modal>

      <Modal isOpen={isMaterialGuideOpen} onClose={() => setIsMaterialGuideOpen(false)} title="Material guide" data-id="element-954">
        <div className="space-y-6" data-id="element-955">
          {materials.map(m => <div key={m.id} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0" data-id="element-956">
              <h4 className="font-bold text-[#0A1628] mb-1" data-id="element-957">{m.label}</h4>
              <p className="text-sm text-slate-600" data-id="element-958">{m.desc}</p>
              {m.id === 'polyester' && <p className="text-xs text-slate-400 mt-1" data-id="element-959">
                  Available in White and Silver
                </p>}
            </div>)}
          <div className="pt-4 mt-4 border-t border-slate-100" data-id="element-960">
            <a href="/contact" className="w-full flex items-center justify-center px-6 py-3 bg-[#0A1628] text-white font-bold rounded-xl hover:bg-[#0052CC] transition-colors" data-id="element-961">
              Still unsure? Contact us for guidance
            </a>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isBulkModalOpen} onClose={() => setIsBulkModalOpen(false)} title="Bulk pricing enquiry" data-id="element-962">
        <form className="space-y-4" data-id="element-963">
          <p className="text-sm text-slate-500 mb-4" data-id="element-964">
            Looking for large quantities? Get in touch for our best trade rates.
          </p>
          <input type="text" placeholder="Name" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" data-id="element-965" />
          <input type="email" placeholder="Email Address" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" data-id="element-966" />
          <input type="tel" placeholder="Phone Number" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" data-id="element-967" />
          <input type="number" placeholder="Estimated Quantity" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" data-id="element-968" />
          <button className="w-full py-3 bg-[#0052CC] text-white font-bold rounded-xl hover:bg-[#0040A0]" data-id="element-969">
            Request Trade Rates
          </button>
        </form>
      </Modal>
    </div>;
}